import { Stack } from '@mui/material';
import { ComponentType, useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilCallback, useRecoilState } from 'recoil';
import {
    BodyContourIcon,
    BoolIcon,
    CouchIcon,
    InterpolateIcon,
    MarginIcon,
    RingIcon,
} from '~/assets/workspace-icons';
import ToolOptionsDock from '~/components/toolbar/shared/ToolOptionsDock';
import UnavailableHeldToolRelease from '~/components/toolbar/tools/ContouringTools/UnavailableHeldToolRelease';
import {
    isParkedContouringTool,
    useContouringToolsParked,
} from '~/components/toolbar/shared/useParkedContouringTool';
import {
    type PostProcessingTab,
    type OperationPanelState,
    heldToolState,
    onlyChangesView,
    operationBusyState,
    selectedToolState,
    type Tools,
} from '~/state/atoms/toolsAtoms';
import BooleanOperationContent from './boolean/BooleanOperationContent';
import CouchOperationContent from './couch/CouchOperationContent';
import { POST_PROCESSING_TAB_META } from './postProcessing/postProcessingTabs';
import PostProcessingOperationContent from './postProcessing/PostProcessingOperationContent';
import MarginOperationContent from './margin/MarginOperationContent';
import RingOperationContent from './ring/RingOperationContent';
import InterpolationOperation from './slice/InterpolationOperation';
import ThresholdSegmentationOperationContent from './thresholdSegmentation/ThresholdSegmentationOperationContent';
import { useSetOperationPanelUnlessBusy } from './useSetOperationPanelUnlessBusy';

/**
 * Width in px of every operation panel. 400 keeps a boolean step row
 * (letter, operation, structure, remove) on one line with the structure
 * name readable.
 */
const OPERATION_PANEL_WIDTH = 400;

interface UnifiedOperationPanelProps {
    panelState: OperationPanelState;
    onClose: () => void;
}

/**
 * What the header says and shows per operation. Titles carry their own
 * message ids rather than reusing the toolbar tooltip ids:
 * `toolbar.booleanOperation.tooltip` is plural ("Boolean Operations")
 * while this header reads "Boolean Operation" singular, and the couch
 * panel names the activity ("Insert couch structures") where its toolbar
 * tooltip names the tool. Post Processing names the OPERATION, not the
 * panel, through its tab's own meta: which operation you are in is the
 * one thing the header can say that nothing else does. The record is
 * keyed on every panel type, so a new panel without a title here is a
 * compile error, not a blank header over a CT.
 */
const OPERATION_PANEL_META: Record<
    Exclude<OperationPanelState['type'], 'closed' | 'post-processing'>,
    { titleId: string; Icon: ComponentType }
> = {
    'boolean-operation': {
        titleId: 'operationPanel.title.booleanOperation',
        Icon: BoolIcon,
    },
    margin: { titleId: 'operationPanel.title.margin', Icon: MarginIcon },
    ring: { titleId: 'operationPanel.title.ring', Icon: RingIcon },
    'threshold-segmentation': {
        titleId: 'operationPanel.title.thresholdSegmentation',
        Icon: BodyContourIcon,
    },
    couch: { titleId: 'operationPanel.title.couch', Icon: CouchIcon },
    interpolation: {
        titleId: 'operationPanel.title.interpolation',
        Icon: InterpolateIcon,
    },
};

function getPanelMeta(
    panelState: OperationPanelState
): { titleId: string; Icon: ComponentType } | null {
    switch (panelState.type) {
        case 'closed':
            return null;
        case 'post-processing': {
            const { labelId, Icon } = POST_PROCESSING_TAB_META[panelState.tab];
            return { titleId: labelId, Icon };
        }
        default:
            return OPERATION_PANEL_META[panelState.type];
    }
}

export default function UnifiedOperationPanel({
    panelState,
    onClose,
}: UnifiedOperationPanelProps) {
    const intl = useIntl();
    const [closeDisabled, setCloseDisabled] =
        useRecoilState(operationBusyState);
    const setPanelStateUnlessBusy = useSetOperationPanelUnlessBusy();
    const [selectedTool, setSelectedTool] = useRecoilState(selectedToolState);
    const [heldTool, setHeldTool] = useRecoilState(heldToolState);
    const toolsParked = useContouringToolsParked();
    // The tool on the views as the panel last saw or set it. A change from
    // it is a pick.
    const previousTool = useRef(selectedTool);
    // The held tool as the panel last saw it.
    const previousHeldTool = useRef(heldTool);
    // The tool to arm as the operation closes, if no tool is held then: the
    // user's latest pick, or the tool armed as the operation opened.
    const toolOnClose = useRef(selectedTool);
    // Whether the operation closes once it is free, for the write tool
    // picked or held last.
    const closeOnceFree = useRef(false);

    // Puts a tool on the views for the panel, which is not a pick.
    const armTool = useCallback(
        (tool: Tools) => {
            previousTool.current = tool;
            setSelectedTool(tool);
        },
        [setSelectedTool]
    );

    // One tool at a time. Picking a tool that works on the structures
    // closes the operation, so that tool takes over and its options show.
    // A viewing tool leaves the operation open, and so does a tool the
    // operation's region box holds parked, which never arms. While the
    // operation previews or writes, a write tool picked is held off the
    // views (see pickedToolState), and the operation closes once it is
    // free, which arms the held tool. Letting go of a held tool, by a pick
    // of a viewing tool, by Escape, or because its entry turns disabled
    // (see UnavailableHeldToolRelease), keeps the operation open and leaves
    // the tool on the views to arm as it closes. This effect runs before
    // the one below, so a change of operation reads the latest pick.
    useEffect(() => {
        const letGo = previousHeldTool.current !== null && heldTool === null;
        const newlyHeld =
            heldTool !== null && heldTool !== previousHeldTool.current;
        previousHeldTool.current = heldTool;
        if (selectedTool !== previousTool.current || letGo) {
            previousTool.current = selectedTool;
            toolOnClose.current = selectedTool;
            closeOnceFree.current =
                !onlyChangesView(selectedTool) &&
                !(toolsParked && isParkedContouringTool(selectedTool));
        }
        if (newlyHeld) {
            closeOnceFree.current = true;
        }
        if (closeOnceFree.current && !closeDisabled) {
            closeOnceFree.current = false;
            onClose();
        }
    }, [closeDisabled, heldTool, onClose, selectedTool, toolsParked]);

    // Opening an operation, or changing to another, puts the views on Pan,
    // so no tool that writes is left armed beside the operation. A viewing
    // tool stays. Closing the operation arms the held tool, else the user's
    // latest pick, or the tool armed as it opened.
    useEffect(() => {
        if (!onlyChangesView(previousTool.current)) {
            armTool('pan');
        }
    }, [armTool, panelState.type]);
    const armOnClose = useRecoilCallback(
        ({ snapshot, set }) =>
            () => {
                const held = snapshot.getLoadable(heldToolState).getValue();
                const tool = held ?? toolOnClose.current;
                set(heldToolState, null);
                previousHeldTool.current = null;
                previousTool.current = tool;
                set(selectedToolState, tool);
            },
        []
    );
    useEffect(() => () => armOnClose(), [armOnClose]);

    // The busy flag outlives the panel, so the panel clears it as it
    // closes, whatever its operation last reported.
    useEffect(() => () => setCloseDisabled(false), [setCloseDisabled]);

    // A tab change unmounts the tab that is writing, so it is refused while
    // the operation is busy, as every other switch of operation is.
    const handlePostProcessingTabChange = useCallback(
        (tab: PostProcessingTab) => {
            setPanelStateUnlessBusy({ type: 'post-processing', tab });
        },
        [setPanelStateUnlessBusy]
    );

    // Escape closes the operation, or, while it is busy, lets go of a held
    // tool. An Escape something else already handled, such as a tool
    // cancelling its action in progress, does neither.
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key !== 'Escape' || e.defaultPrevented) {
                return;
            }
            if (closeDisabled) {
                setHeldTool(null);
            } else {
                onClose();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [closeDisabled, onClose, setHeldTool]);

    const handleClose = useCallback(() => {
        if (!closeDisabled) {
            onClose();
        }
    }, [closeDisabled, onClose]);

    const meta = getPanelMeta(panelState);
    const title = meta ? intl.formatMessage({ id: meta.titleId }) : '';

    const content = (
        <Stack spacing={2}>
            {panelState.type === 'boolean-operation' && (
                <BooleanOperationContent onClose={onClose} />
            )}

            {panelState.type === 'margin' && (
                <MarginOperationContent
                    mode={panelState.mode}
                    onClose={onClose}
                    onPreventCloseChange={setCloseDisabled}
                />
            )}

            {panelState.type === 'ring' && (
                <RingOperationContent
                    onClose={onClose}
                    onPreventCloseChange={setCloseDisabled}
                />
            )}

            {panelState.type === 'threshold-segmentation' && (
                <ThresholdSegmentationOperationContent
                    onClose={onClose}
                    onPreventCloseChange={setCloseDisabled}
                />
            )}

            {panelState.type === 'couch' && (
                <CouchOperationContent
                    onClose={onClose}
                    onPreventCloseChange={setCloseDisabled}
                />
            )}

            {panelState.type === 'interpolation' && (
                <InterpolationOperation onClose={onClose} />
            )}

            {panelState.type === 'post-processing' && (
                <PostProcessingOperationContent
                    tab={panelState.tab}
                    onTabChange={handlePostProcessingTabChange}
                    onPreventCloseChange={setCloseDisabled}
                    tabsDisabled={closeDisabled}
                />
            )}
        </Stack>
    );

    return (
        <>
            <UnavailableHeldToolRelease />
            <ToolOptionsDock
                kind="operation"
                title={title}
                icon={meta && <meta.Icon />}
                width={OPERATION_PANEL_WIDTH}
                anchor={panelState.type}
                onClose={handleClose}
                closeDisabled={closeDisabled}
            >
                {content}
            </ToolOptionsDock>
        </>
    );
}
